import {ActionButtonsHeader} from './ActionButtonsHeader.web-form'
import {THead} from './THead'
import {Pagination} from '../../../content/pagination/Pagination'
import React, {FC, useCallback, useMemo, useState} from 'react'
import {Headers, Sort, SortKeys, SortOrder} from '../types'
import {ExtendedCampaignInterface} from '../../../../../app/pages/mailing/campaigns/types'
import {paginate} from '../../../content/pagination/types'
import {Get} from '../../../../../setup'
import {Link} from 'react-router-dom'
import {PATH_MAILING} from '../../../../../app/routing/paths'
import {useMailing} from '../../../../../app/@types'
import {KTSVG} from '../../../../helpers'
import {ActionButtonsColumnWebForm} from './ActionButtonsColumn.web-form'
import {SearchedData} from '../../../../../app/features'

interface DataTableWebFormInterface {
  headers: Headers<ExtendedCampaignInterface>[];
  data: ExtendedCampaignInterface[];
  favorite?: boolean;
}

const TypesChampagne = [
  {id: 1, name: 'Brouillons', color: 'secondary'},
  {id: 2, name: 'Programmées', color: 'primary'},
  {id: 3, name: 'En cour d\'envoi', color: 'warning'},
  {id: 4, name: 'Envoyées', color: 'success'},
  {id: 5, name: 'Archivées', color: 'info'},
  {id: 6, name: 'Favoris', color: 'warning'},
]

const ADD_TO_FAVORITES = process.env.REACT_APP_MAILING_ADD_TO_FAVORITES || 'v2/campaigns-favorite'

const DataTableWebForm: FC<DataTableWebFormInterface> = ({
                                                           headers,
                                                           data,
                                                           favorite,
                                                         }) => {

  const {triggerReload} = useMailing()

  const [sortKey, setSortKey] = useState<SortKeys<ExtendedCampaignInterface>>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const dataSorted: ExtendedCampaignInterface[] = useMemo(() => {
    return Sort<ExtendedCampaignInterface>({data, sortKey, reverse: sortOrder === 'desc'})
  }, [data, sortKey, sortOrder])

  const filteredData = useMemo(() => {
    return SearchedData<ExtendedCampaignInterface>(dataSorted, searchQuery)
  }, [dataSorted, searchQuery])

  const paginateData = useMemo(
    () => paginate<ExtendedCampaignInterface>({currentPage, itemsPerPage, data: filteredData}),
    [currentPage, itemsPerPage, filteredData],
  )

  const AddToFavorites = useCallback(async (id: number) => {

    const endpoint = `${ADD_TO_FAVORITES}/${id}`
    const {status} = await Get<any>({endpoint})
    if (status === 200) triggerReload()
  }, [triggerReload])

  const handleCheckboxChange = useCallback((id: number) => {
    setSelectedItems(prevSelected =>
      prevSelected.includes(id) ? prevSelected.filter(itemId => itemId !== id) : [...prevSelected, id],
    )
    setSelectAll(data.length === selectedItems.length + 1)
  }, [data.length, selectedItems.length])

  const renderBadge = useCallback(
    (status: string) => {
      const type = TypesChampagne.find(type => type.name === status)
      return type ? <span className={`badge badge-${type.color}`}>{status}</span> : null
    },
    [],
  )

  return (
    <>
      <ActionButtonsHeader selectAll={selectAll}
                           setSelectAll={setSelectAll}
                           setSelectedItems={setSelectedItems}
                           selectedItems={selectedItems}
                           setSearchQuery={setSearchQuery}
                           paginateData={paginateData}
      />

      <div className="table-responsive">
        <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
          <THead
            headers={headers}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            firstChildren={<th className="min-w-50px" />}
            children={<th className="min-w-100px" />}
          />
          <tbody>
          {
            paginateData.map((item, index) => (
              <tr key={index}>
                <td>
                  <div className="form-check form-check-sm form-check-custom form-check-solid">
                    <input
                      className="form-check-input me-3"
                      type="checkbox"
                      value={item.id}
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleCheckboxChange(item.id)}
                    />
                    {favorite && (
                      <button className="btn btn-icon btn-sm ml-3"
                              onClick={() => AddToFavorites(item.id)}
                      >
                        <KTSVG path={'/media/icons/duotune/general/gen029.svg'}
                               className={`svg-icon-1 svg-icon-${item.favorite ? 'warning' : 'gray-300'}`}
                        />
                      </button>
                    )}
                  </div>

                </td>
                {headers.map((header, headerIndex) => (
                  <td key={headerIndex}>
                    {
                      header.key === 'name' ? (
                        <Link to={`${PATH_MAILING.campaign.show}/${item.id}`}
                              className="text-dark text-hover-primary fw-bolder fs-6">
                          {item.name}
                        </Link>
                      ) : header.key === '_status' ? (
                        renderBadge(item._status)
                      ) : (
                        item[header.key as keyof ExtendedCampaignInterface]
                      )}
                  </td>
                ))}
                <td className="text-end">
                  <ActionButtonsColumnWebForm id={item.id} />
                </td>
              </tr>
            ))
          }
          </tbody>
        </table>
      </div>


      <Pagination itemsPerPage={itemsPerPage}
                  totalItems={filteredData.length}
                  setCurrentPage={setCurrentPage}
                  setItemsPerPage={setItemsPerPage}
      />
    </>
  )
}

export {DataTableWebForm}